/**
 * DecksPage - デッキ一覧画面
 * ImageSet（現在のデッキ）を新しい順で表示
 * React Suspenseを使用した非同期データ読み込み対応
 */

import { useEffect, Suspense } from 'react'
import { useStore } from '../store'
import { useSuspenseSets } from '../features/sets/hooks/useSuspenseSets'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { logger } from '../lib/logger'

// Loading component for Suspense
function LoadingFallback() {
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto mb-4 border-4 border-gray-600 border-t-white rounded-full animate-spin" />
        <p className="text-gray-400">デッキを読み込み中...</p>
      </div>
    </div>
  )
}

// Main decks content component
function DecksContent() {
  // Suspense対応のフックでデータ取得
  const { sets: decks } = useSuspenseSets({
    sortBy: 'createdAt',
    sortOrder: 'desc'
  })
  
  const navigateTo = useStore(state => state.navigateTo)
  const setCurrentSetId = useStore(state => state.setCurrentSetId)

  // 戻るボタンの処理
  useEffect(() => {
    window.history.pushState({ page: 'decks' }, '', null)
    
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault()
      navigateTo('home')
    }
    
    window.addEventListener('popstate', handlePopState)
    
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [navigateTo])

  // デッキを選択して再生ページへ
  const handleDeckSelect = (deckId: string) => {
    logger.log('[DecksPage] Selected deck:', deckId)
    // 状態を更新してから画面遷移
    // SimplifiedPlaybackPageで独立してデータを取得するので、
    // 状態更新と画面遷移を同期的に実行
    setCurrentSetId(deckId)
    navigateTo('simplified-playback')
  }

  // 日付フォーマット
  const formatDate = (date: Date | string) => {
    const d = date instanceof Date ? date : new Date(date)
    return d.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* ヘッダー */}
      <div className="sticky top-0 bg-gray-900 border-b border-gray-800 z-10">
        <div className="flex items-center p-4">
          <button
            onClick={() => navigateTo('home')}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="ml-4 text-xl font-semibold">デッキ</h1>
        </div>
      </div>

      {/* デッキ一覧 */}
      <div className="p-4">
        {decks.length === 0 ? (
          <div className="text-center text-gray-500 mt-20">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <p>まだデッキがありません</p>
            <p className="text-sm mt-2">撮影を開始してデッキを作成しましょう</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {decks.map((deck, index) => (
              <button
                key={deck.id}
                onClick={() => handleDeckSelect(deck.id)}
                className="bg-gray-900 hover:bg-gray-800 rounded-lg p-4 transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium">
                      {deck.name || `デッキ ${decks.length - index}`}
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">
                      {formatDate(deck.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* タイプ表示 */}
                    <span className={`px-2 py-1 rounded text-xs ${
                      deck.type === 'circular' 
                        ? 'bg-blue-900 text-blue-300' 
                        : 'bg-gray-700 text-gray-300'
                    }`}>
                      {deck.type === 'circular' ? '円形' : '線形'}
                    </span>
                    {/* 矢印 */}
                    <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// メインコンポーネント（ErrorBoundaryとSuspenseでラップ）
export function DecksPage() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <DecksContent />
      </Suspense>
    </ErrorBoundary>
  )
}